import { mkdir, writeFile, access } from "node:fs/promises";
import { dirname, join } from "node:path";
import type { ComponentIR } from "../ir.js";
import { bindingsGenerator } from "./bindings-generator.js";
import { claudeGenerator } from "./claude-generator.js";
import { cliGenerator } from "./cli-generator.js";
import { clientGenerator } from "./client-generator.js";
import { embeddedDslGenerator } from "./embedded-dsl-generator.js";
import { handlerGenerator } from "./handler-generator.js";
import { runtimeGenerator } from "./runtime-generator.js";
import { typesGenerator } from "./types-generator.js";
import type { GeneratedFile, GenerateOptions, Generator } from "./types.js";

export type { GeneratedFile, GenerateOptions, Generator } from "./types.js";
export { typesGenerator } from "./types-generator.js";
export { clientGenerator } from "./client-generator.js";
export { runtimeGenerator } from "./runtime-generator.js";
export { cliGenerator } from "./cli-generator.js";
export { handlerGenerator } from "./handler-generator.js";
export { embeddedDslGenerator } from "./embedded-dsl-generator.js";
export { bindingsGenerator } from "./bindings-generator.js";
export { claudeGenerator } from "./claude-generator.js";

const ALL_GENERATORS: Generator[] = [
  typesGenerator,
  clientGenerator,
  runtimeGenerator,
  cliGenerator,
  handlerGenerator,
  embeddedDslGenerator,
  bindingsGenerator,
  claudeGenerator,
];

/**
 * Run all code generators for a compiled Component IR.
 */
export function generateAll(
  ir: ComponentIR,
  options: GenerateOptions,
): GeneratedFile[] {
  return ALL_GENERATORS.flatMap((gen) => gen.generate(ir, options));
}

async function fileExists(path: string): Promise<boolean> {
  try {
    await access(path);
    return true;
  } catch {
    return false;
  }
}

/**
 * Write generated files to disk, respecting overwrite policy.
 * - `overwrite: true` — always write (generated/ artifacts)
 * - `overwrite: false` — skip if target already exists (handlers, cli stub)
 */
export async function writeGeneratedFiles(
  files: GeneratedFile[],
  outputDir: string,
): Promise<{ written: string[]; skipped: string[]; warnings: string[] }> {
  const written: string[] = [];
  const skipped: string[] = [];
  const warnings: string[] = [];

  for (const file of files) {
    const targetPath = join(outputDir, file.path);

    if (file.warning) {
      warnings.push(file.warning);
    }

    if (!file.overwrite && (await fileExists(targetPath))) {
      skipped.push(file.path);
      continue;
    }

    if (!file.content && !file.overwrite) {
      skipped.push(file.path);
      continue;
    }

    await mkdir(dirname(targetPath), { recursive: true });
    await writeFile(targetPath, file.content, "utf-8");
    written.push(file.path);
  }

  return { written, skipped, warnings };
}
